/**
 * Header.js
 * "The Toolbox" - Minimalist top navigation.
 */
import { HEADER_ACTIONS } from '../../config/navigation.js';
import { renderIcon } from '../../utils/icons.js';
import { InAppNotificationService } from '../../services/in-app-notification.service.js'; // [NEW]

export class Header {
    constructor(container, { onToggleDrawer }) {
        this.container = container;
        this.onToggleDrawer = onToggleDrawer;
        this.render();
        this.initBadgeSubscription(); // [NEW]

        // [FIX] Listen for Profile Updates (SPA Sync)
        window.addEventListener('user-profile-update', () => {
            this.updateHeader();
        });
    }

    // [NEW] Real-time Badge
    initBadgeSubscription() {
        const user = window.user;
        if (!user) return;

        // Subscribe
        this.unsubscribe = InAppNotificationService.subscribeUnreadCount(user.uid, (count) => {
            this.setNotificationBadge(count > 0);
        });
    }

    // Cleanup if needed (SPA navigation usually keeps header, but good practice)
    disconnect() {
        if (this.unsubscribe) this.unsubscribe();
    }


    render() {
        const header = document.createElement('header');
        // [EMERGENCY FIX] Z-Index 9999: Highest priority as requested.
        header.className = "fixed top-0 left-0 right-0 h-14 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between px-4 z-[50] transition-transform duration-300";
        header.id = "app-header";

        // Left Section: Logo & Menu
        const leftSection = document.createElement('div');
        leftSection.className = "flex items-center gap-2";

        // Hamburger Menu
        const menuBtn = document.createElement('button');
        menuBtn.className = "p-2 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors";
        menuBtn.setAttribute('aria-label', 'Menu');
        const menuIcon = document.createElement('div');
        renderIcon(menuIcon, 'Menu', { class: "w-6 h-6" });
        menuBtn.appendChild(menuIcon);
        menuBtn.onclick = () => {
            // Standard Toggle
            if (this.onToggleDrawer) this.onToggleDrawer();
        };
        leftSection.appendChild(menuBtn);
        header.appendChild(leftSection);

        // Center: Logo
        const title = document.createElement('h1');
        // Changed: Added cursor-pointer, removed pointer-events-none, added hover effect
        title.className = "text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600 absolute left-1/2 transform -translate-x-1/2 cursor-pointer hover:opacity-80 transition-opacity";
        title.innerText = "SSULWHA";
        title.onclick = () => {
            if (window.router) window.router.navigate('/');
            else window.location.href = '/';
        };
        header.appendChild(title);

        // Right: Actions Container
        this.rightContainer = document.createElement('div');
        this.rightContainer.className = "flex items-center gap-1"; // Base gap

        // Render Initial Content
        this.updateHeader();

        header.appendChild(this.rightContainer);
        this.container.appendChild(header);
    }

    updateHeader() {
        if (!this.rightContainer) return;
        this.rightContainer.innerHTML = ''; // Clear existing

        // 1. Standard Actions (Search, Notification)
        HEADER_ACTIONS.forEach(action => {
            const btn = document.createElement('button');
            btn.id = `header-btn-${action.id}`;
            btn.className = "p-2 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors";
            btn.setAttribute('aria-label', action.id === 'search' ? 'Search' : action.id === 'notification' ? 'Notifications' : action.id);
            renderIcon(btn, action.icon, { class: "w-5 h-5" });

            if (action.id === 'notification') {
                btn.className += " relative"; // For Badge
            }
            btn.onclick = () => {
                window.dispatchEvent(new CustomEvent('header-action', { detail: action.action }));
            };
            this.rightContainer.appendChild(btn);
        });

        // 2. [NEW] User Profile Avatar (If Logged In)
        const user = window.user;
        if (user) {
            const avatarContainer = document.createElement('div');
            // Added margin-left for visual separation from notification icon
            avatarContainer.className = "ml-1 w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center overflow-hidden border border-zinc-200 dark:border-zinc-700 cursor-pointer hover:ring-2 hover:ring-violet-500 transition-all";
            avatarContainer.setAttribute('role', 'button');
            avatarContainer.setAttribute('aria-label', 'Profile menu');

            if (user.photoURL) {
                const img = document.createElement('img');
                img.src = user.photoURL;
                img.alt = user.displayName || 'Profile';
                img.className = "w-full h-full object-cover";
                avatarContainer.appendChild(img);
            } else {
                const initial = document.createElement('span');
                initial.className = "text-xs font-bold text-violet-600 dark:text-violet-300";
                initial.innerText = user.displayName ? user.displayName[0].toUpperCase() : 'U';
                avatarContainer.appendChild(initial);
            }

            // Interaction: Toggle Drawer (Same as hamburger)
            avatarContainer.onclick = () => {
                if (this.onToggleDrawer) this.onToggleDrawer();
            };

            this.rightContainer.appendChild(avatarContainer);
        }
    }

    setNotificationBadge(show) {
        const btn = document.getElementById('header-btn-notification');
        if (!btn) return;

        let badge = btn.querySelector('.notif-badge');
        if (show) {
            if (!badge) {
                badge = document.createElement('div');
                badge.className = "notif-badge absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white dark:border-zinc-900";
                btn.appendChild(badge);
            }
        } else {
            if (badge) badge.remove();
        }
    }
}
